import { useMutation } from "@apollo/client/react";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { DryRunOptions, JobLogLevel } from "~/common/types";
import type { JobRun } from "~/components/job-log";
import { config } from "~/config";
import {
  GetBatchEvidencesJobsDocument,
  SubmitBatchChangeRequestsJobDocument,
  type SubmitBatchChangeRequestsJobMutation,
  type SubmitBatchChangeRequestsJobMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type UseSubmitBatchChangeRequestsJobProps = {
  jobId: string;
  refetch: () => void;
  setDryRunJobRunId?: (jobRunId: string | null) => void;
  setLogDialogJobRun?: (jobRun: JobRun | null) => void;
  jobName?: string;
  skipConfirm?: boolean;
};

export const useSubmitBatchChangeRequestsJob = ({
  jobId,
  refetch,
  setDryRunJobRunId,
  setLogDialogJobRun,
  jobName,
  skipConfirm = false,
}: UseSubmitBatchChangeRequestsJobProps) => {
  const { t } = useTranslation();
  const actionWithConfirm = useActionWithConfirm();
  const [processing, setProcessing] = React.useState<{ value: boolean; dryRun: boolean }>({
    value: false,
    dryRun: false,
  });
  const [submitJob] = useMutation<SubmitBatchChangeRequestsJobMutation, SubmitBatchChangeRequestsJobMutationVariables>(
    SubmitBatchChangeRequestsJobDocument,
    {
      // The list query requires tenantId + filter variables; callers already trigger a targeted `refetch()`.
      // Keeping this empty avoids accidental bare refetches that would fail variable validation.
      refetchQueries: [],
    },
  );
  const { tenantId } = useTenant();
  const submit = useCallback(
    async (dryRun: boolean, dryRunOptions?: DryRunOptions, logLevel?: JobLogLevel) => {
      setProcessing({ value: true, dryRun });
      const res = await actionWithConfirm({
        skipConfirm: dryRun || skipConfirm,
        confirmMessage: t("feature.dataImportProjects.detail.jobs.confirmChangeRequestsJobRun"),
        successMessage: t("feature.dataImportProjects.detail.notify.submitJobSuccessfully"),
        action: async () => {
          if (!tenantId) {
            throw new Error("Tenant is not resolved");
          }

          const { data } = await submitJob({
            variables: { input: { tenantId, jobId, dryRun, dryRunOptions, logLevel } },
          });
          const ret = data?.cnutSubmitBatchChangeRequestsJob;
          if (ret?.__typename === "ClientError") {
            throw new Error(`error code: ${ret.code} message: ${ret.message}`);
          } else if (ret?.__typename === "BatchChangeRequestsJobSubmitOutput") {
            return ret;
          } else {
            throw new Error(t("ui.unknown"));
          }
        },
        successAction: (result) => {
          // if submit successful and jobRunId is available
          if (result && result.jobRunId) {
            // if ENVIRONMENT=local and dryRun, show result directly
            if (config.environment === "local" && dryRun && setDryRunJobRunId) {
              setDryRunJobRunId(result.jobRunId as string);
            } else if (setLogDialogJobRun && jobName) {
              // otherwise, open log-viewer dialog
              setLogDialogJobRun({
                jobRunId: result.jobRunId as string,
                jobName,
                dryRun,
              });
            }
          }
        },
        finallyAction: () => {
          setProcessing({ value: false, dryRun: false });
        },
      });
      refetch();

      return res;
    },
    [
      actionWithConfirm,
      jobId,
      submitJob,
      t,
      refetch,
      setProcessing,
      setDryRunJobRunId,
      setLogDialogJobRun,
      jobName,
      skipConfirm,
      tenantId,
    ],
  );

  return {
    submit,
    processing,
  };
};
